/**
 * One step's runner (2.J deliverables 10 and 11): preview, apply and the state between them. Every
 * transition goes through a ref first, so a second click while the first is in flight sends
 * nothing. A 409 returns to editing and keeps the input; the person previews again. After an
 * apply and after an Undo, the step's batches and every read its `touches` names are read again.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { batchesKey, type StepApi } from './data.js';
import { useDraft, useDraftStore } from './drafts.js';
import {
  applied,
  applying,
  canApply,
  edited,
  failed,
  failureOf,
  INITIAL_STEP,
  previewed,
  previewing,
  type StepState,
} from './model/wizard.js';

export interface StepRunner<Req> {
  readonly state: StepState;
  readonly canApply: boolean;
  edit(): void;
  preview(request: Req): Promise<void>;
  apply(): Promise<void>;
  /** After an undo: the batches and the touched reads are read again and the last request is previewed again. */
  refresh(): Promise<void>;
}

export function useStep<Req>(stepApi: StepApi<Req>): StepRunner<Req> {
  const client = useQueryClient();
  const store = useDraftStore();
  const stateKey = `step:${stepApi.kind}`;
  const requestKey = `request:${stepApi.kind}`;
  const [state] = useDraft<StepState>(stateKey, INITIAL_STEP);
  // Read from the store, not from a ref of this mount: an answer that lands after the step was
  // left and drawn again belongs to the mounted step, and a second click sees the same phase.
  const read = useCallback(() => store.get<StepState>(stateKey, INITIAL_STEP), [store, stateKey]);
  const move = useCallback((next: StepState) => store.set(stateKey, next), [store, stateKey]);

  const edit = useCallback(() => move(edited(read())), [move, read]);
  const reread = useCallback(
    () =>
      Promise.all(
        [batchesKey(stepApi.kind), ...stepApi.touches].map((queryKey) =>
          client.invalidateQueries({ queryKey }),
        ),
      ),
    [client, stepApi],
  );

  const preview = useCallback(
    async (request: Req) => {
      const phase = read().phase;
      if (phase === 'previewing' || phase === 'applying') return;
      store.set(requestKey, request);
      move(previewing());
      try {
        const answer = await stepApi.preview(request);
        move(previewed(read(), answer));
      } catch (error) {
        move(failed(read(), failureOf(error)));
      }
    },
    [stepApi, store, requestKey, move, read],
  );

  const apply = useCallback(async () => {
    const request = store.get<Req | null>(requestKey, null);
    const hash = read().preview?.previewHash;
    if (request === null || hash === undefined || !canApply(read())) return;
    move(applying(read()));
    try {
      await stepApi.apply(request, hash);
      move(applied());
    } catch (error) {
      move(failed(read(), failureOf(error)));
      return;
    }
    await reread();
  }, [stepApi, store, requestKey, move, read, reread]);

  const refresh = useCallback(async () => {
    await reread();
    const request = store.get<Req | null>(requestKey, null);
    if (request !== null) await preview(request);
  }, [store, requestKey, preview, reread]);

  return { state, canApply: canApply(state), edit, preview, apply, refresh };
}
